import { has, prop } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';

import { initAppWithDelegation } from '@gef-ui/delegations/actions';
import { revokeComingFromUrl, setComingFromUrl, setDelegationsFromQuery } from '@gef-ui/delegations/utils';
import { getIasc } from '@gef-ui/features/iasc/actions';
import { mergeAppContext } from '@gef-ui/redux-common/actions/appContext';
import { mergeApmConfig } from '@gef-ui/statemanagement-redux/apm/actions';

import { setPostInitLoaderActive, setPostInitLoaderInactive } from 'app/configurator/actions/loader';
import {
	FETCH_REPRESENTED_USER_INFO_PATH,
	FETCH_USER_INFO_PATH,
	MIGRATION_IN_PROGRESS_CODE,
} from 'app/configurator/constants';
import { loadAppCSS } from 'app/configurator/utils';
import { get, getDataWithoutTokenWrapper } from 'features/common/fetch/actions';
import { appUser, configJSON } from 'features/common/fetch/beEndpoints';
import { getIsLoaded, getMetaData } from 'features/common/fetch/selectors';
import { setBaseUrl } from 'features/common/fetch/utils';
import { bridge } from 'features/common/iasBridge/actions';
import { getParameterByName } from 'utils';
import history from 'utils/history';

import { saveConfig, saveUrls } from './actions';

export const initializeApp = async (dispatch) => {
	setComingFromUrl();
	setDelegationsFromQuery();

	try {
		await dispatch(loadConfig);
		await dispatch(loadInitApp);
	} catch (error) {
		const parsed = prop('parsedContent', error);
		if (error !== false) {
			if (parsed?.code === MIGRATION_IN_PROGRESS_CODE) {
				history.push('/migration-in-progress');
				return Promise.resolve();
			} else if (has('parsedContent', error)) {
				history.push('/login/error', { error: prop('parsedContent', error) });
				return Promise.resolve();
			} else {
				history.push('/login/error', { error });
				return Promise.resolve();
			}
		}
	}
};

export const loadConfig = (dispatch) => {
	setBaseUrl(window.location.origin);
	return dispatch(
		getDataWithoutTokenWrapper(configJSON, {
			onSuccess: [({ body }) => dispatch(saveConfig(body)), ({ body }) => dispatch(saveUrls(body))],
		}),
	).then(({ body }) => {
		setBaseUrl(body.backendUrl);
		if (!isNilOrEmpty(body?.apm)) {
			dispatch(
				mergeApmConfig({
					serverUrl: body.apm.serverUrl,
					environment: body.apm.environment,
					serviceName: body.apm.serviceName,
				}),
			);
		}
	});
};

export const fetchUserInfo = (onSuccess) => (dispatch) => {
	return dispatch(
		get(appUser, {
			uniqueReqID: 'userInfo',
			onSuccess,
		}),
	);
};

export const fetchUserInfoWithRepresentedUser =
	(onSuccess, { company, login }) =>
	(dispatch) => {
		return dispatch(
			get(appUser, { uniqueReqID: 'representedUserInfo', onSuccess, queryParams: { company, login } }),
		);
	};

export const loadInitApp = async (dispatch) => {
	await dispatch(
		initAppWithDelegation({
			userInfoEndpoint: appUser,
			continueAction: loadApplication,
			createFetchAction: fetchUserInfo,
			createFetchActionWithRepresentedUser: fetchUserInfoWithRepresentedUser,
			getIsOriginalUserFetchLoaded: getIsLoaded(FETCH_USER_INFO_PATH),
			getCalledUserInfoCompany: getMetaData(FETCH_REPRESENTED_USER_INFO_PATH),
		}),
	);
};

export const loadApplication =
	(comeFromUrl = '/') =>
	async (dispatch) => {
		dispatch(setPostInitLoaderActive());

		const targetPage = getParameterByName('targetPage', comeFromUrl);
		if (!isNilOrEmpty(targetPage)) {
			const iascId = getParameterByName('IASC', comeFromUrl);
			try {
				await dispatch(getIasc(iascId));
			} catch (error) {
				if (has('parsedContent', error)) {
					history.push('/login/error', { error: prop('parsedContent', error) });
					revokeComingFromUrl();
					return false;
				}
			}
			dispatch(mergeAppContext({ targetPage }));
			await dispatch(bridge());
			dispatch(setPostInitLoaderInactive());
			await loadAppCSS();
		} else {
			await loadAppCSS();
			revokeComingFromUrl();
			dispatch(setPostInitLoaderInactive());
			history.push(comeFromUrl);
		}
	};
